import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import {
  searchFormsRecyclingValidation,
  schemaInternationalPhoneSearchForm,
} from '@ltvco/refresh-lib/utils';
import {
  SearchReportResponseData,
  PhoneSearchParams,
  useSession,
  phoneSearch,
} from '@ltvco/refresh-lib/v1';
import { InternationalPhoneInput } from '@ltvco/refresh-lib/v2';
import {
  useForm,
  useMutation,
  yupResolver,
  useFeatureIsOn,
  Controller,
} from '@ltvco/refresh-lib/vendors';
import React, { useContext, useCallback } from 'react';

import { constants } from 'appConstants';

import { SearchButton } from '../components';
import { SearchProps } from '../utils';

import {
  PhoneFormContainer,
  PhoneSearchFieldContainer,
  FormHelperErrorText,
} from './components';

export interface PhoneFormData {
  phone: string;
}

export const PhoneSearch: React.FC<SearchProps> = ({
  isMobile,
  placeholderText,
  onSearchSuccess,
  placement = 'dashboard',
}) => {
  const {
    session: { account },
  } = useSession();
  const { logError, trackEventGA4 } = useContext(AppConfig);

  const { phoneSearchCountryCodes, phoneSearchPlaceholders } =
    constants.internationalPhoneInput;
  const allowedPlaceholders = phoneSearchPlaceholders || {
    placeholderText: placeholderText,
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<PhoneFormData>({
    resolver: yupResolver(schemaInternationalPhoneSearchForm),
    defaultValues: {
      phone: '',
    },
  });
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');

  const phoneQuery = useMutation<
    SearchReportResponseData,
    Error,
    PhoneSearchParams
  >({
    mutationFn: phoneSearch,
    onSuccess: (data, variables) => {
      onSearchSuccess &&
        onSearchSuccess({
          permalink: data.report.permalink,
          reportType: 'phone',
          searchParams: `phone=${encodeURIComponent(variables.phone)}`,
        });
    },
    onError: (error) => {
      logError('Phone Search', error);
    },
  });

  const onPhoneSearchSubmit = useCallback(
    ({ phone }: { phone: string }) => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'phone',
        search_by: 'phone',
        search_from: `${placement}_search`,
      });

      phoneQuery.mutate({
        phone: phone,
        report_flags: JSON.stringify(['dd_plan%3Dfalcon']),
        showPartialAddresses: isPhonePartialAddress,
      });
      reset();
    },
    [
      account,
      trackEventGA4,
      phoneQuery,
      isPhonePartialAddress,
      reset,
      placement,
    ]
  );

  const renderMobileForm = useCallback(() => {
    return (
      <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
        <Box sx={{ width: '100%', position: 'relative' }}>
          <PhoneSearchFieldContainer error={Boolean(errors.phone)}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <InternationalPhoneInput
                  field={field}
                  name="phone"
                  error={Boolean(errors.phone)}
                  autoFocus={true}
                  allowedCountryCodes={phoneSearchCountryCodes}
                  placeholders={phoneSearchPlaceholders}
                />
              )}
            />
          </PhoneSearchFieldContainer>
          {errors.phone && (
            <FormHelperErrorText>{errors.phone.message}</FormHelperErrorText>
          )}
        </Box>

        <SearchButton type="submit" fullWidth>
          Search
        </SearchButton>
      </Grid>
    );
  }, [control, errors, phoneSearchCountryCodes, phoneSearchPlaceholders]);

  const renderDesktopForm = useCallback(() => {
    return (
      <PhoneFormContainer>
        <Box flexGrow={1}>
          <PhoneSearchFieldContainer container error={Boolean(errors.phone)}>
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <InternationalPhoneInput
                    field={field}
                    name="phone"
                    error={Boolean(errors.phone)}
                    autoFocus={true}
                    allowedCountryCodes={phoneSearchCountryCodes}
                    placeholders={allowedPlaceholders}
                  />
                )}
              />
            </Grid>
          </PhoneSearchFieldContainer>
          {errors.phone && (
            <FormHelperErrorText>{errors.phone.message}</FormHelperErrorText>
          )}
        </Box>
        <SearchButton type="submit">Search</SearchButton>
      </PhoneFormContainer>
    );
  }, [control, errors, phoneSearchCountryCodes, allowedPlaceholders]);

  return (
    <form onSubmit={handleSubmit(onPhoneSearchSubmit)}>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
    </form>
  );
};
